import { queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import { ApiUserInline, ApiUserProfile, ApiVenueAllStaffData } from "griptonite-api-client";
import { useEffect, useRef } from "react";
import { usePrevious } from "react-use";

import queryClient from "@/core/queryClient";

import api from "../core/api";

export const meProfileQuery = queryOptions({
  queryKey: ["profiles", "me"],
  queryFn: api.profiles.getMeProfile,
});

export const useMeProfileQuery = () => {
  return useQuery(meProfileQuery);
};

export const useStaffProfileQuery = (uuid: string) => {
  return useQuery({
    queryKey: ["profiles", uuid],
    queryFn: async () => {
      const profile = await api.profiles.getProfile(uuid);
      return profile as ApiUserProfile | ApiUserInline;
    },
    placeholderData: () => {
      const allStaffData = queryClient.getQueryData<ApiVenueAllStaffData>(["venues", "allStaff"]);
      return allStaffData?.staff.find((staff) => staff.user.uuid === uuid)?.user;
    },
  });
};

export const useProfilesSearchQuery = (search: string) => {
  const abortControllerRef = useRef(new AbortController());

  const prevSearch = usePrevious(search);

  useEffect(() => {
    if (prevSearch !== search) {
      abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();
    }
  }, [search, prevSearch]);

  return useQuery({
    queryKey: ["profiles", "search", search],
    queryFn: async () => {
      if (!search || search.length < 3) {
        return { data: [], metadata: { count: 0 } };
      }
      const result = await api.profiles.searchProfiles(search, {
        signal: abortControllerRef.current.signal,
      });
      return result;
    },
  });
};

export const useInviteStaffMutation = () => {
  return useMutation({
    mutationFn: api.accounts.inviteStaff,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["venues", "allStaff"],
      });
    },
  });
};
